import { useState, useEffect } from "react";
import { useAuthContext } from '@/auth';
import { useIdleTimerContext } from "react-idle-timer";

// The provider in Main.jsx shows the prompt at IDLE_LIMIT - PROMPT_BEFORE_IDLE
// and fires onIdle (logout) at IDLE_LIMIT. While the prompt is open, passive
// activity does not reset the timer — only "Stay Logged In" (activate) does.
// The backend enforces its own 30-minute idle cutoff on the token
// (sanctum.idle_expiration), so this client limit must stay inside that.
export const IDLE_LIMIT = 15 * 60 * 1000; // 15 minutes
export const PROMPT_BEFORE_IDLE = 2 * 60 * 1000; // warn 2 minutes before logout

const IdleTracker = ({ onLogout }) => {
  const { auth, loading } = useAuthContext();
  const { isPrompted, getRemainingTime, activate } = useIdleTimerContext();
  const [remainingMs, setRemainingMs] = useState(null);

  // Another tab logged out (idle or manual): follow it to the login screen.
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'forceLogout') {
        localStorage.setItem("logoutTriggered", "true");
        window.location.reload();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    if (!auth || loading) return;

    const interval = setInterval(() => {
      setRemainingMs(isPrompted() ? getRemainingTime() : null);
    }, 500);

    return () => clearInterval(interval);
  }, [auth, loading, isPrompted, getRemainingTime]);

  if (remainingMs === null) return null;

  const minutes = Math.floor(remainingMs / 60000);
  const seconds = String(Math.floor((remainingMs % 60000) / 1000)).padStart(2, '0');

  return (
    <div className="modal-wrapper modal-animate-fade bg-opacity-50 flex justify-center items-center z-[9999]">
      <div className="modal-body modal-animate-scale w-4/5 max-w-md relative">
        <h2 className="text-lg font-medium text-gray-900">Are you still there?</h2>
        <p className="text-sm mb-2.5 mt-1">
          You&apos;ve been inactive for a while. For your security, you&apos;ll be signed out
          in <span className="font-semibold">{minutes}:{seconds}</span> unless you
          choose to stay logged in.
        </p>
        <button onClick={() => activate()} className="mr-2 btn btn-sm btn-light btn-outline">Stay Logged In</button>
        <button onClick={onLogout} className="btn btn-sm btn-light btn-danger btn-outline shrink-0">Log Out Now</button>
      </div>
    </div>
  );
};

export default IdleTracker;
